import { useEffect, useState } from "react";
import { api, C } from "../lib/api";

const LABEL = {
  flop_monotone: "Flop all one suit", flop_rainbow: "Flop three suits", flop_paired: "Paired flop", flop_trips: "Trips on the flop",
  river_flush_possible: "Flush possible by the river", holdem_pocket_pair: "Pocket pair dealt (Hold'em)", holdem_suited: "Suited hand dealt (Hold'em)", holdem_aces: "Pocket aces dealt",
};
const pct = (x, d = 2) => (x == null ? "—" : `${(x * 100).toFixed(d)}%`);
// two-sided 95% band for a binomial proportion: is the observed rate within noise of the mathematics?
const within = (obs, exp, n) => n > 0 && Math.abs(obs - exp) <= 1.96 * Math.sqrt((exp * (1 - exp)) / n);

// Live evidence: observed card frequencies vs. exact probability, all-in equity vs. results by cohort, chain status.
export const FairnessStats = () => {
  const [s, setS] = useState(null);
  useEffect(() => { api.get(C.ApiRoutes.FAIR_STATS).then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s) return <p className="text-slate-500 text-sm" data-testid="fair-stats-loading">Loading live figures…</p>;
  return (
    <div className="flex flex-col gap-5" data-testid="fair-stats">
      <div className="flex flex-wrap gap-3 text-xs">
        <span data-testid="fair-stats-hands" className="rounded-full bg-white/5 px-3 py-1 text-amber-100"><b className="font-mono">{s.hands.toLocaleString()}</b> hands tallied</span>
        <span data-testid="fair-stats-chained" className="rounded-full bg-white/5 px-3 py-1 text-amber-100"><b className="font-mono">{s.chained_hands.toLocaleString()}</b> hash-chained records</span>
        <span data-testid="fair-stats-allin" className="rounded-full bg-white/5 px-3 py-1 text-amber-100"><b className="font-mono">{s.allin_spots.toLocaleString()}</b> all-in spots analysed</span>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-slate-400 mb-2">Card frequencies · observed vs. a perfectly random deck</div>
        <table className="w-full text-xs" data-testid="fair-textures">
          <thead><tr className="text-slate-500 text-left"><th className="py-1 font-normal">Event</th><th className="font-normal text-right">Count</th><th className="font-normal text-right">Observed</th><th className="font-normal text-right">Expected</th><th className="font-normal text-right">95% band</th></tr></thead>
          <tbody>
            {s.textures.map((t) => (
              <tr key={t.key} data-testid={`fair-texture-${t.key}`} className="border-t border-white/5">
                <td className="py-1.5 text-slate-200">{LABEL[t.key] || t.key}</td>
                <td className="text-right font-mono text-slate-300">{t.count.toLocaleString()} / {t.of.toLocaleString()}</td>
                <td className="text-right font-mono text-amber-100">{pct(t.observed)}</td>
                <td className="text-right font-mono text-slate-300">{pct(t.expected)}</td>
                <td className="text-right">{t.of < 30 ? <span className="text-slate-500">too few yet</span> : within(t.observed, t.expected, t.of) ? <span className="text-emerald-300">within</span> : <span className="text-amber-300">outside</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-slate-400 mb-1">All-in equity vs. what was actually won</div>
        <p className="text-[11px] text-slate-500 mb-2">For every all-in with cards to come we compute each player's exact share of the pot (the equity) and compare it with the chips they actually won. A fair deal lands near 100% for every group — new accounts, seasoned ones, humans and bots alike. No "honeymoon", no "drain".</p>
        {s.equity.length === 0 && <p className="text-slate-500 text-xs" data-testid="fair-equity-empty">No all-in run-outs recorded yet.</p>}
        {s.equity.length > 0 && (
          <table className="w-full text-xs" data-testid="fair-equity">
            <thead><tr className="text-slate-500 text-left"><th className="py-1 font-normal">Cohort</th><th className="font-normal text-right">Spots</th><th className="font-normal text-right">Expected chips</th><th className="font-normal text-right">Won</th><th className="font-normal text-right">Won / expected</th></tr></thead>
            <tbody>
              {s.equity.map((e) => (
                <tr key={e.cohort} data-testid={`fair-equity-${e.cohort.replace(/[^a-z0-9]+/gi, "-")}`} className="border-t border-white/5">
                  <td className="py-1.5 text-slate-200">{e.cohort}</td>
                  <td className="text-right font-mono text-slate-300">{e.n}</td>
                  <td className="text-right font-mono text-slate-300">{Math.round(e.expected).toLocaleString()}</td>
                  <td className="text-right font-mono text-amber-100">{e.won.toLocaleString()}</td>
                  <td className="text-right font-mono text-amber-100">{e.ratio == null ? "—" : `${(e.ratio * 100).toFixed(1)}%`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {s.latest && <div className="text-[11px] text-slate-500 font-mono break-all" data-testid="fair-latest-hash">latest record hash · {s.latest.hand_id} · {s.latest.record_hash}</div>}
    </div>
  );
};
